import React                                   from 'react'
import {Box, Typography, useMediaQuery, useTheme} from '@mui/material'
import {alpha}                                 from '@mui/material/styles'
import {fmtTimeLeft, trialMessage, TRIAL_TIER, trialTier} from '../../util/trial-banner'

/*
 * The free-trial countdown, in two presentations of the same state (169).
 *
 *   variant="navbar"    a compact chip, fixed at top centre between the logo and the hamburger.
 *                       Every study-mode screen.
 *   variant="greeting"  the full banner with the tiered copy, in the dashboard's greeting row.
 *
 * ONE AT A TIME, never both. The dashboard owns the greeting card; every other screen gets the chip,
 * and ExpiringPrepKit holds that rule (170).
 *
 * Both read util/trial-banner for the tier and the words, so they cannot disagree about how long is
 * left. Neither owns a clock; `msLeft` is passed in from useExpiryCountdown.
 */

// amber normally, red inside the last minute -- the one moment the banner is genuinely urgent
const bannerAccent = (msLeft, custom) =>
	trialTier(msLeft) === TRIAL_TIER.ONE ? custom.status.danger : custom.hud.streak

const Pill = ({label, accent, contrast, custom, sx}) => (
	<Box component="span" sx={{
		flexShrink: 0,
		px: 1.5,
		py: 0.5,
		borderRadius: 999,
		background: accent,
		color: contrast,
		fontSize: custom.fontSize.xxs,
		fontWeight: custom.fontWeight.bold,
		textTransform: 'uppercase',
		letterSpacing: '0.08em',
		whiteSpace: 'nowrap',
		...sx,
	}}>
		{label}
	</Box>
)

const TrialBanner = ({variant, msLeft, isTrial, onBuy}) => {
	const theme = useTheme()
	const custom = theme.custom
	/*
	 * Below sm the chip has roughly 200px to live in, which does not hold a pill, a time and a
	 * button. The compact form drops the pill and shortens the label.
	 */
	const compact = useMediaQuery(theme.breakpoints.down('sm'))

	const accent = bannerAccent(msLeft, custom)
	const contrast = theme.palette.getContrastText(accent)
	const cta = isTrial ? 'Get Birdsy Now' : 'Renew Now'

	/*
	 * The button is PURPLE, not the banner's accent (182).
	 *
	 * The accent is amber, and red inside the last minute — it is the countdown's urgency, and
	 * putting it on the button made the one thing to click read as part of the warning rather than
	 * as an action, in a brown that belongs to nothing else in the app. The button is the same
	 * indigo every primary action uses, so it is recognisable as the button here.
	 *
	 * The pill, the border and the time keep the accent, so the urgency still changes at one minute
	 * — just not under the student's pointer.
	 */
	const button = (
		<Box
			component="button"
			type="button"
			data-ga={`trial-banner-${variant}`}
			onClick={onBuy}
			sx={{
				font: 'inherit',
				flexShrink: 0,
				px: variant === 'navbar' ? 1.5 : 2.25,
				py: variant === 'navbar' ? 0.5 : 0.875,
				cursor: 'pointer',
				borderRadius: `${custom.radius.sm}px`,
				border: `1px solid ${custom.indigo.primary}`,
				background: custom.indigo.primary,
				color: '#fff',
				fontSize: variant === 'navbar' ? custom.fontSize.xs : custom.fontSize.sm,
				fontWeight: custom.fontWeight.semibold,
				whiteSpace: 'nowrap',
				transition: 'background 150ms, border-color 150ms',
				'&:hover': {background: custom.indigo.hover, borderColor: custom.indigo.hover},
				'&:focus-visible': {outline: `2px solid ${custom.indigo.primary}`, outlineOffset: 2},
			}}
		>
			{compact && variant === 'navbar' ? 'Get Birdsy' : cta}
		</Box>
	)

	if (variant === 'navbar') {
		/*
		 * Fixed and centred inside the top band. zIndex 1500 clears study mode's own control band at
		 * 1400, which is the layer the brand mark and hamburger sit on when there is no Navbar.
		 *
		 * maxWidth is set by the LOGO side, doubled. A centred element is limited by whichever
		 * shoulder is wider, not by the sum of the two, so the hamburger's narrower side buys nothing.
		 */
		return (
			<Box sx={{
				position: 'fixed',
				top: {xs: 8, sm: 12},
				left: '50%',
				transform: 'translateX(-50%)',
				zIndex: 1500,
				maxWidth: {xs: 'calc(100vw - 170px)', sm: 'calc(100vw - 220px)', md: 680},
				display: 'flex',
				alignItems: 'center',
				gap: {xs: 0.75, sm: 1.25},
				px: {xs: 1, sm: 1.25},
				py: {xs: 0.5, sm: 0.625},
				background: custom.bg.hud,
				border: `1px solid ${alpha(accent, 0.55)}`,
				borderRadius: 999,
				boxShadow: custom.shadow.pop,
			}}>
				{!compact && (
					<Pill label={isTrial ? 'Free trial' : 'PrepKit'} accent={accent} contrast={contrast} custom={custom}/>
				)}
				<Typography noWrap sx={{
					minWidth: 0,
					fontSize: custom.fontSize.xs,
					fontWeight: custom.fontWeight.semibold,
					color: accent,
				}}>
					{fmtTimeLeft(msLeft)}
				</Typography>
				{button}
			</Box>
		)
	}

	/*
	 * The greeting-row card (170). Sits to the right of "Welcome back", where Start Studying and the
	 * Tutor Style picker used to be.
	 *
	 * maxWidth, and no bottom margin: it is a flex item beside the greeting rather than a band across
	 * the page, so it must not grow into the greeting's space. The row already wraps, so on a narrow
	 * screen this drops below the greeting and takes the full width there.
	 */
	const {lead, time, tail} = trialMessage(msLeft, isTrial)

	return (
		<Box sx={{
			display: 'flex',
			alignItems: 'center',
			flexWrap: 'wrap',
			gap: {xs: 1.25, sm: 1.5},
			px: {xs: 1.5, sm: 2},
			py: {xs: 1.25, sm: 1.5},
			maxWidth: {xs: '100%', md: 560},
			/*
			 * Transparent in dark mode (186), and only here — the navbar chip above keeps its fill on
			 * every screen, because it floats over the page rather than sitting in a row with a
			 * heading beside it.
			 *
			 * bg.hud is a raised panel colour, which in dark mode made a solid block sitting next to
			 * "Welcome back" and competing with it. The accent border already draws the card; letting
			 * the page through is enough to keep it a card without making it a slab. Light mode keeps
			 * the fill, where the same block reads as a pale tint rather than a heavy one.
			 */
			background: theme.palette.mode === 'dark' ? 'transparent' : custom.bg.hud,
			/*
			 * The HUD cards' hairline, not the accent (188). This sits in the same row as Exam
			 * Readiness and its neighbours; an amber outline made it a different kind of object,
			 * when it is one more card in that set.
			 *
			 * The urgency is still carried — by the pill, the countdown and the button — and it still
			 * turns red inside the last minute. It just no longer draws the box.
			 */
			border: `1px solid ${accent}`,
			borderRadius: `${custom.radius.lg}px`,
			boxShadow: custom.shadow.card,
		}}>
			<Pill label={isTrial ? 'Free trial' : 'PrepKit'} accent={accent} contrast={contrast} custom={custom}/>

			<Typography sx={{
				flex: '1 1 200px',
				minWidth: 0,
				fontSize: custom.fontSize.sm,
				color: custom.text.main,
				lineHeight: 1.45,
			}}>
				{lead}
				<Box component="span" sx={{color: accent, fontWeight: custom.fontWeight.bold}}>
					{time}
				</Box>
				{tail}
			</Typography>

			{button}
		</Box>
	)
}

export default TrialBanner
